/**
 * Card carousel: infinite loop and autoplay, both opt-in per section.
 *
 * Loop ([data-oddit-loop]) clones the card set once before and once after the
 * real one, parks the scroll on the real set, then shifts scrollLeft by exactly
 * one set width whenever the viewport drifts into a clone set. The three sets
 * are identical, so the shift is invisible and the track scrolls forever both
 * ways. Without loop, autoplay rewinds to the start when it reaches the end.
 *
 * Below 990px the track is a static grid, the clones are hidden by CSS and
 * nothing scrolls, so both features sit idle there.
 */
(function () {
  var TRACK_SELECTOR = '.oddit-section-card-carousel__track';
  var CARD_SELECTOR = '.oddit-section-card-carousel__card';
  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  function toArray(list) {
    return Array.prototype.slice.call(list);
  }

  // Clones are inserted after the global reveal observer has already run, so
  // they keep no reveal classes — otherwise they sit at opacity 0 forever.
  function makeClone(card) {
    var node = card.cloneNode(true);
    node.classList.add('oddit-section-card-carousel__card--clone');
    node.setAttribute('aria-hidden', 'true');
    node.setAttribute('tabindex', '-1');
    toArray(node.querySelectorAll('*')).concat([node]).forEach(function (el) {
      el.removeAttribute('id');
      el.removeAttribute('data-shopify-editor-block');
      if (el.classList) {
        el.classList.remove('oddit-reveal');
        el.classList.remove('is-visible');
      }
      if (el.style) {
        el.style.removeProperty('--oddit-stagger-index');
      }
    });
    return node;
  }

  function initTrack(track) {
    if (track._odditCarouselCtrl) return;

    var loop = track.hasAttribute('data-oddit-loop');
    var autoplay =
      track.getAttribute('data-autoplay') === 'true' && !reducedMotion.matches;
    if (!loop && !autoplay) return;

    var reals = toArray(track.querySelectorAll(CARD_SELECTOR));
    if (reals.length < 2) return;

    if (loop) {
      var firstReal = reals[0];
      reals.forEach(function (card) {
        track.insertBefore(makeClone(card), firstReal);
      });
      reals.forEach(function (card) {
        track.appendChild(makeClone(card));
      });
    }

    var ctrl = new AbortController();
    var opts = { passive: true, signal: ctrl.signal };
    var setWidth = 0;
    var stepWidth = 0;
    var resizeTimer = null;
    var autoTimer = null;
    var settleTimer = null;
    var autoScrolling = false;
    var paused = false;

    // One set width including the gap that follows it: the offset between a
    // card and its clone one set earlier. Immune to the track's scroll padding.
    // Step is the same measure across a single card.
    function measure() {
      var kids = track.children;
      setWidth = loop ? reals[0].offsetLeft - kids[0].offsetLeft : 0;
      stepWidth = kids.length > 1 ? kids[1].offsetLeft - kids[0].offsetLeft : 0;
    }

    function scrolls() {
      return track.scrollWidth > track.clientWidth + 1;
    }

    // A shift mid-animation cancels whatever smooth scroll is running, so the
    // wrap waits out the drag settle and the autoplay step.
    function busy() {
      return (
        autoScrolling ||
        track.classList.contains('is-settling') ||
        track.classList.contains('is-dragging')
      );
    }

    // Mandatory snap has to be off for the shift itself: re-targeting from the
    // pre-shift position lets the snap engine flick the track back a slide,
    // which is the flicker you see when the wrap lands mid-swipe.
    function shift(delta) {
      track.style.scrollSnapType = 'none';
      track.scrollLeft += delta;
      requestAnimationFrame(function () {
        track.style.scrollSnapType = '';
      });
    }

    function wrap() {
      if (!setWidth || !scrolls() || busy()) return;
      if (track.scrollLeft < setWidth * 0.5) shift(setWidth);
      else if (track.scrollLeft > setWidth * 1.5) shift(-setWidth);
    }

    function park() {
      measure();
      if (setWidth && scrolls()) shift(setWidth - track.scrollLeft);
    }

    // Holds the wrap off until the smooth scroll lands, then re-checks. The
    // spare clone set is a whole half-set deep, so waiting is never a hazard.
    function holdWrap() {
      autoScrolling = true;
      clearTimeout(settleTimer);
      settleTimer = setTimeout(function () {
        autoScrolling = false;
        wrap();
      }, 700);
    }

    function tick() {
      if (paused || !scrolls() || busy() || document.hidden) return;
      if (!stepWidth) measure();
      if (!stepWidth) return;
      var atEnd = track.scrollLeft >= track.scrollWidth - track.clientWidth - 1;
      if (!loop && atEnd) track.scrollTo({ left: 0, behavior: 'smooth' });
      else track.scrollBy({ left: stepWidth, behavior: 'smooth' });
      holdWrap();
    }

    function startAutoplay() {
      stopAutoplay();
      if (!autoplay) return;
      var seconds = parseInt(track.getAttribute('data-autoplay-speed'), 10) || 5;
      autoTimer = setInterval(tick, seconds * 1000);
    }

    function stopAutoplay() {
      clearInterval(autoTimer);
      autoTimer = null;
    }

    track.addEventListener('scroll', wrap, opts);
    window.addEventListener(
      'resize',
      function () {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(park, 150);
      },
      opts
    );

    if (autoplay) {
      ['pointerenter', 'focusin'].forEach(function (name) {
        track.addEventListener(name, function () {
          paused = true;
        }, opts);
      });
      ['pointerleave', 'focusout'].forEach(function (name) {
        track.addEventListener(name, function () {
          paused = false;
        }, opts);
      });
      startAutoplay();
    }

    park();
    requestAnimationFrame(park);

    track._odditCarouselCtrl = ctrl;
    track._odditCarouselTeardown = function () {
      ctrl.abort();
      stopAutoplay();
      clearTimeout(resizeTimer);
      clearTimeout(settleTimer);
      track._odditCarouselCtrl = null;
      track._odditCarouselTeardown = null;
    };
  }

  function initAll(root) {
    var scope = root && root.querySelectorAll ? root : document;
    scope.querySelectorAll(TRACK_SELECTOR).forEach(initTrack);
  }

  initAll();

  document.addEventListener('shopify:section:load', function (event) {
    if (event.target) initAll(event.target);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    if (!event.target || !event.target.querySelectorAll) return;
    event.target.querySelectorAll(TRACK_SELECTOR).forEach(function (track) {
      if (track._odditCarouselTeardown) track._odditCarouselTeardown();
    });
  });
})();
